/**
 * What a device's ports are and how to show them, shared by the API (insights, Ask AI) and
 * the app so both name and read ports the same way. No Node or browser APIs here.
 */

/** Enough of a port's set-up to name it and read tank levels. */
export interface PortInfo {
  kind: string;
  channel: number;
  name?: string;
  scale?: { low: number; high?: number; tankFull?: number; tankEmpty?: number };
  tank?: { full: number; empty: number };
  widget?: Widget;
  displayRange?: { min: number; max: number };
  /** Unit of measure the reading is in (Modbus points and other makes). */
  unit?: string;
  enabled?: boolean;
}

export interface DeviceInfo {
  model: string;
  ports: PortInfo[];
}

const FMS_KINDS = new Set(["ai", "di", "do", "ro", "ao"]);
export const isFmsBoard = (model: string) => model === "multipro" || model === "mini";

/** The reading "port" a port set-up describes: ai1, ro2 on FMS boards; the channel name on other makes. */
export function portKey(p: Pick<PortInfo, "kind" | "channel">): string {
  return FMS_KINDS.has(p.kind) && p.channel > 0 ? `${p.kind}${p.channel}` : p.kind;
}

const KIND_NAMES: Record<string, string> = {
  ai: "Analog input",
  di: "Digital input",
  do: "Digital output",
  ro: "Relay",
  ao: "Analog output",
};

const CHANNEL_NAMES: Record<string, string> = {
  rssi: "Signal strength",
  snr: "Signal quality",
  device_rssi: "Signal at device",
  device_snr: "Signal quality at device",
  battery: "Battery",
  // An FMS board's own temperature (its reply to QtempX), inside the enclosure: not a sensor.
  temp: "Board temperature",
  temperature: "Temperature",
  humidity: "Humidity",
  gps_lat: "GPS latitude",
  gps_lon: "GPS longitude",
  depth: "Depth",
  distance: "Distance",
  moisture: "Soil moisture",
  ec: "Conductivity",
  pressure: "Pressure",
  wind_speed: "Wind speed",
  wind_direction: "Wind direction",
  rainfall_total: "Rain total",
  co2: "CO₂",
};

export function portLabel(device: DeviceInfo, port: string): string {
  const cfg = device.ports.find((p) => portKey(p) === port);
  if (cfg?.name) return cfg.name;
  if (readsDistance(cfg)) return "Tank level";
  if (CHANNEL_NAMES[port]) return CHANNEL_NAMES[port]!;
  const m = /^([a-z]{2})(\d+)$/.exec(port);
  if (m && KIND_NAMES[m[1]!]) return `${KIND_NAMES[m[1]!]} ${m[2]}`;
  const words = port.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A switching command described by the output's name ("Bore pump on", else "Relay 1 on")
 * instead of its code ("ro1 on"), for the command list and the activity log.
 */
export function namedCommand<C extends { description: string }>(command: C, device: DeviceInfo, port: string): C {
  return command.description.startsWith(`${port} `) ? { ...command, description: portLabel(device, port) + command.description.slice(port.length) } : command;
}

/** On/off ports: FMS digital inputs, outputs and relays; Milesight GPIO and valve channels. */
export function isOnOff(device: Pick<DeviceInfo, "model">, port: string): boolean {
  if (isFmsBoard(device.model)) return /^(di|do|ro)\d+$/.test(port);
  // Milesight controllers: di_n / do_n (UC300), gpio_n / gpio_out_n (UC50x), valves (UC51x).
  return /^(gpio_(input|output)|gpio_out|gpio|valve|di|do)_\d+$/.test(port);
}

/**
 * Readings about the device itself rather than what it measures. "temp" is an FMS board's own
 * temperature, kept apart from connected sensors (a weather station's or PT100's temperature).
 */
export function isDeviceHealth(port: string): boolean {
  return port === "rssi" || port === "snr" || port === "device_rssi" || port === "device_snr" || port === "battery" || port === "temp";
}

type TankPort = Pick<PortInfo, "scale" | "tank">;

/** A tank port's readings when full and empty: set on the port, or the older "tank full at" on an analog input. */
export function tankRange(port: TankPort | undefined): { full: number; empty: number } | undefined {
  if (port?.tank && port.tank.full !== port.tank.empty) return port.tank;
  const full = port?.scale?.tankFull;
  if (!full) return undefined;
  const empty = port.scale!.tankEmpty ?? (port.scale!.low < full ? port.scale!.low : 0);
  return full === empty ? undefined : { full, empty };
}

/** % full for a tank port. */
export function tankPercent(port: TankPort | undefined, value: number): number | undefined {
  const t = tankRange(port);
  if (!t) return undefined;
  return Math.max(0, Math.min(100, Math.round((100 * (value - t.empty)) / (t.full - t.empty))));
}

/**
 * Depth of water for a sensor that measures down to it (full is the smaller reading): the
 * distance when empty less the distance now. Other tank sensors already read the level.
 */
export function tankLevel(port: TankPort | undefined, value: number): number {
  const t = tankRange(port);
  if (!t || t.full > t.empty) return value;
  return Math.max(0, t.empty - value);
}

/** True when the port's raw reading is a distance down to the water rather than a level. */
export function readsDistance(port: TankPort | undefined): boolean {
  const t = tankRange(port);
  return !!t && t.full < t.empty;
}

export type Widget = "gauge" | "tank" | "thermo" | "graph" | "number" | "onoff" | "light" | "soil" | "wind" | "rose" | "flow" | "rain";

/**
 * How a reading is shown: what was chosen in set-up, otherwise a sensible pick from what the
 * port is (a tank, an on/off, wind direction, a temperature, soil moisture) or a plain number.
 */
export function widgetFor(device: DeviceInfo, port: string, unit?: string): Widget {
  const cfg = device.ports.find((p) => portKey(p) === port);
  if (cfg?.widget) return cfg.widget;
  // Inputs get a light, outputs a switch.
  if (isOnOff(device, port)) return /^(do|ro)\d+$|^valve_|^gpio_out|^do_/.test(port) ? "onoff" : "light";
  if (isRainTotal(device, port)) return "rain";
  if ((cfg as { litresPerPulse?: number } | undefined)?.litresPerPulse || /counter|pulse/.test(port) || unit === "L" || unit === "kL") return "flow";
  if (tankRange(cfg)) return "tank";
  if (isWindDirection(device, port)) return "rose";
  if (isSoilMoisture(device, port)) return "soil";
  if (unit === "°C" || /temperature|pt100/.test(port)) return "thermo";
  return "number";
}

/** The range a gauge or thermometer is drawn over: set, the sensor's range, or a default. */
export function widgetRange(port: PortInfo | undefined, widget: Widget, value: number): { min: number; max: number } {
  if (port?.displayRange) return port.displayRange;
  if (port?.scale?.high !== undefined && port.scale.high > port.scale.low) return { min: port.scale.low, max: port.scale.high };
  if (widget === "thermo") return { min: Math.min(-5, value), max: Math.max(45, value) };
  if (widget === "soil") return { min: 0, max: 60 };
  // Nothing to go on: 0 to a round number above the value.
  const top = Math.max(1, Math.abs(value) * 1.5);
  const step = 10 ** Math.floor(Math.log10(top));
  return { min: Math.min(0, value), max: Math.ceil(top / step) * step };
}

// ------------------------------------------------------------------ weather and soil

const nameOf = (device: DeviceInfo, port: string) => device.ports.find((p) => portKey(p) === port)?.name ?? "";

/** A wind direction reading: Milesight's wind_direction, or a Modbus weather station point named so. */
export function isWindDirection(device: DeviceInfo, port: string): boolean {
  if (port === "wind_direction") return true;
  const cfg = device.ports.find((p) => portKey(p) === port);
  // "Direction" alone could be a lateral's travel; in degrees it's the wind.
  return /wind\s*dir/i.test(cfg?.name ?? "") || (/direction/i.test(cfg?.name ?? "") && cfg?.unit === "°");
}

/** A soil moisture reading: an EM500-SMTC's moisture, or a point named for it (VSM, VSW, VWC). */
export function isSoilMoisture(device: DeviceInfo, port: string): boolean {
  if (/moisture/.test(port)) return true;
  return /moist|\bvs[mw]\b|\bvwc\b/i.test(nameOf(device, port));
}

/**
 * A weather station's wind direction and the speed that goes with it. On a board reading
 * several Modbus slaves the speed is the one on the same slave as the direction (mb1_3_1 and
 * mb1_3_2), so two stations on one board don't get mixed up.
 */
export function windPorts(device: DeviceInfo, readingPorts: readonly string[] = []): { direction?: string; speed?: string } {
  const keys = [...new Set([...device.ports.filter((p) => p.enabled !== false).map(portKey), ...readingPorts])];
  const direction = keys.find((k) => device.ports.find((p) => portKey(p) === k)?.widget === "rose") ?? keys.find((k) => isWindDirection(device, k));
  if (!direction) return {};
  const slave = /^(mb\d+_\d+)_/.exec(direction)?.[1];
  const speeds = keys.filter((k) => k !== direction && (k === "wind_speed" || /wind\s*speed|^speed/i.test(nameOf(device, k))));
  const speed = speeds.find((k) => slave && k.startsWith(`${slave}_`)) ?? speeds[0];
  return { direction, ...(speed ? { speed } : {}) };
}

/** A device's soil moisture readings (several on a probe with depths), in port order. */
export function soilPorts(device: DeviceInfo, readingPorts: readonly string[] = []): string[] {
  const keys = [...new Set([...device.ports.filter((p) => p.enabled !== false).map(portKey), ...readingPorts])];
  return keys.filter((k) => widgetFor(device, k) === "soil").sort((a, b) => (parseInt(a.replace(/\D/g, "")) || 0) - (parseInt(b.replace(/\D/g, "")) || 0));
}

/** Refill and full points for a soil moisture reading (its "Show as" range), and whether they've been set. */
export function soilBand(device: DeviceInfo, port: string): { refill: number; full: number; set: boolean } {
  const r = device.ports.find((p) => portKey(p) === port)?.displayRange;
  return r ? { refill: r.min, full: r.max, set: true } : { refill: 25, full: 40, set: false };
}

/**
 * How many days until a drying soil reaches its refill point, from how fast it fell over the
 * last three days. Undefined when it isn't drying (just watered, or rain).
 */
export function daysToRefill(points: ReadonlyArray<{ at: number; value: number }>, refill: number, now: number): number | undefined {
  const recent = points.filter((p) => now - p.at <= 3 * 86_400_000);
  if (recent.length < 2) return undefined;
  const first = recent[0]!;
  const last = recent[recent.length - 1]!;
  const days = (last.at - first.at) / 86_400_000;
  if (days < 0.5) return undefined;
  const perDay = (first.value - last.value) / days;
  if (perDay <= 0.05 || last.value <= refill) return last.value <= refill ? 0 : undefined;
  return (last.value - refill) / perDay;
}

/** A sentence on where the soil water is: "At 31 %, inside the band. Reaches refill in about 4 days." */
export function soilSummary(value: number, band: { refill: number; full: number }, days: number | undefined, unit = "%"): string {
  const v = `${Math.round(value)} ${unit}`.replace(" %", " %");
  if (value < band.refill) return `At ${v}, below the refill point (${band.refill} ${unit}): time to irrigate.`;
  if (value > band.full) return `At ${v}, above the full point (${band.full} ${unit}): wetter than it needs.`;
  return `At ${v}, inside the band.${days !== undefined && days > 0 ? ` At the rate it's drying it reaches the refill point in about ${Math.max(1, Math.round(days))} day${Math.round(days) === 1 ? "" : "s"}.` : ""}`;
}

/**
 * A rain gauge's running total (Milesight rainfall_total, or a weather station point named
 * rainfall / rain total / accumulated), not its rate (mm/hr).
 */
export function isRainTotal(device: DeviceInfo, port: string): boolean {
  const cfg = device.ports.find((p) => portKey(p) === port);
  const text = `${port} ${cfg?.name ?? ""}`;
  return /rain/i.test(text) && !/rate|intensity|\/\s*h/i.test(`${text} ${cfg?.unit ?? ""}`);
}

/** The rain rate point beside a running total (same Modbus slave), when the station has one. */
export function rainRatePort(device: DeviceInfo, total: string, readingPorts: readonly string[] = []): string | undefined {
  const keys = [...new Set([...device.ports.map(portKey), ...readingPorts])];
  const slave = /^(mb\d+_\d+)_/.exec(total)?.[1];
  const rates = keys.filter((k) => k !== total && /rain/i.test(`${k} ${nameOf(device, k)}`) && /rate|intensity/i.test(`${k} ${nameOf(device, k)}`));
  return rates.find((k) => slave && k.startsWith(`${slave}_`)) ?? rates[0];
}

/**
 * How much a running total went up after `from`: rain fallen, water through a meter. A total
 * that goes back down was reset or rolled over (the gauge resets at midnight, or was replaced),
 * so the new value counts from zero instead of as a fall.
 */
export function riseSince(points: ReadonlyArray<{ at: number; value: number }>, from: number): number {
  let sum = 0;
  let prev: number | undefined;
  for (const p of points) {
    if (p.at < from) {
      prev = p.value;
      continue;
    }
    if (prev !== undefined) sum += p.value >= prev ? p.value - prev : p.value;
    prev = p.value;
  }
  return sum;
}

